import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { getSchedulesByBreakdownId } from '../../../../api/schedule';
import { ScheduleInfo, CreateScheduleParams } from '../../../../types/schedule.types';
import { getCurrentUserInfo } from '../../../../utils/userUtils';
import { useFilmContext } from '../../../../contexts/FilmContext';
import { useCurrentUser } from '../../../../contexts/CurrentUserContext';
import { useScheduleUpdates } from '../../../../hooks/dataUpdates';
import { useDataCache } from '../../../../contexts/BreakdownDataCacheContext';
import './ScheduleSearch.css';

interface ScheduleSearchProps {
  canCreate?: boolean;
  /** 是否可见 */
  visible: boolean;
  /** 关闭回调 */
  onClose: () => void;
  /** 分场ID，用于查询拍摄计划 */
  breakdownID: string | null | undefined;
  /** 弹窗定位位置 */
  position: { top: number; left: number };
  /** 选中拍摄计划的回调（新建时可附带默认打开场次列表） */
  onSelect: (scheduleID: string, options?: { openScenesListTab?: boolean }) => void;
  /** 排除的元素 ref，点击这些元素时不会关闭弹窗 */
  excludeRef?: React.RefObject<HTMLElement>;
}

const ScheduleSearch: React.FC<ScheduleSearchProps> = ({
  visible,
  onClose,
  breakdownID,
  position,
  onSelect,
  excludeRef,
  canCreate = true,
}) => {
  const { t } = useTranslation();
  const { selectedFilmID } = useFilmContext();
  const { setServerSyncedField } = useCurrentUser();
  const { createSchedule } = useScheduleUpdates();
  const { getScheduleCache, subscribeScheduleCache } = useDataCache();
  const [schedulesList, setSchedulesList] = useState<ScheduleInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [newScheduleName, setNewScheduleName] = useState('');
  const [creating, setCreating] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // 从缓存获取 schedule 列表，若无则从 API 获取
  useEffect(() => {
    const fetchSchedules = async () => {
      if (!visible || !breakdownID) {
        setSchedulesList([]);
        return;
      }

      // 先尝试从缓存获取
      const cachedData = getScheduleCache(breakdownID);
      if (cachedData?.schedules && cachedData.schedules.length > 0) {
        setSchedulesList(cachedData.schedules);
        return;
      }

      // 缓存中没有，从 API 获取
      setLoading(true);
      try {
        const response = await getSchedulesByBreakdownId(breakdownID);
        const schedules = Array.isArray(response.data) ? response.data : [];
        setSchedulesList(schedules);
      } catch (error) {
        console.error('获取拍摄计划列表失败:', error);
        setSchedulesList([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSchedules();
  }, [visible, breakdownID, getScheduleCache]);

  // 订阅 schedule 缓存更新
  useEffect(() => {
    if (!visible || !breakdownID) return;

    const updateSchedulesFromCache = () => {
      const cachedData = getScheduleCache(breakdownID);
      if (cachedData?.schedules && cachedData.schedules.length > 0) {
        setSchedulesList(cachedData.schedules);
      }
    };

    // 立即更新一次
    updateSchedulesFromCache();

    // 订阅缓存更新
    const unsubscribe = subscribeScheduleCache(breakdownID, updateSchedulesFromCache);
    return unsubscribe;
  }, [visible, breakdownID, getScheduleCache, subscribeScheduleCache]);

  const handleCreateSchedule = async () => {
    if (!breakdownID || !newScheduleName.trim()) {
      return;
    }

    try {
      setCreating(true);
      const userInfo = getCurrentUserInfo();
      
      const createParams: CreateScheduleParams = {
        scheduleName: newScheduleName.trim(),
        scheduleBelongBreakdownID: breakdownID,
        scheduleFilmID: selectedFilmID || undefined,
        scheduleOwnerID: userInfo.userID,
        scheduleAssigneeID: userInfo.userID,
        scheduleOrgID: userInfo.userOrgID || undefined,
      };

      // 使用统一的更新 hook，自动更新缓存和通知订阅者
      const newScheduleId = await createSchedule(breakdownID, createParams);

      // 与切换拍摄计划一致：更新本地行为缓存并防抖同步到服务器的 userLastUsedScheduleID
      setServerSyncedField('userLastUsedBreakdownID', breakdownID);
      setServerSyncedField('userLastUsedScheduleID', `${breakdownID};${newScheduleId}`);
      
      // 导航到新创建的日程，默认打开右侧「场次列表」
      onSelect(newScheduleId, { openScenesListTab: true });
      
      // 重置状态
      setNewScheduleName('');
      onClose();
    } catch (error) {
      console.error('创建拍摄计划失败:', error);
      alert(t('scheduleSearch.createFailed') + (error instanceof Error ? error.message : t('common.unknownError')));
    } finally {
      setCreating(false);
    }
  };

  // 处理选择已有 schedule
  const handleSelectSchedule = (scheduleID: string) => {
    onSelect(scheduleID);
    onClose();
  };

  // 点击外部关闭
  useEffect(() => {
    if (!visible) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      
      // 如果点击的是容器内部，不关闭
      if (containerRef.current && containerRef.current.contains(target)) {
        return;
      }
      
      // 如果点击的是排除的元素（如触发按钮），不关闭
      if (excludeRef?.current && excludeRef.current.contains(target)) {
        return;
      }
      
      // 其他情况关闭弹窗
      onClose();
    };

    // 使用 setTimeout 延迟执行，避免按钮点击时立即关闭
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [visible, onClose, excludeRef]);

  // 处理回车键创建
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && newScheduleName.trim()) {
      handleCreateSchedule();
    }
  };

  if (!visible) return null;

  return createPortal(
    <div
      className="schedule-search-overlay"
    >
      <div
        ref={containerRef}
        className="schedule-search-container"
        style={{
          '--position-top': `${position.top}px`,
          '--position-left': `${position.left}px`,
        } as React.CSSProperties}
      >
        <div className="schedule-search-dialog">
          {/* Header */}
          {canCreate && <div className="schedule-search-header">
            <input
              type="text"
              placeholder="输入拍摄计划版本或名称"
              value={newScheduleName}
              onChange={(e) => setNewScheduleName(e.target.value)}
              onKeyDown={handleKeyDown}
              className="schedule-search-name-input"
            />
            <button
              className="schedule-search-create-btn"
              onClick={handleCreateSchedule}
              disabled={!newScheduleName.trim() || creating}
            >
              创建并打开
            </button>
          </div>}

          {/* Results List */}
          <div className="schedule-search-results">
            {loading ? (
              <div className="schedule-search-empty">查询中...</div>
            ) : schedulesList.length === 0 ? (
              <div className="schedule-search-empty">
                暂无拍摄计划
              </div>
            ) : (
              schedulesList.map((scheduleRow) => (
                <div
                  key={scheduleRow.scheduleID}
                  className="schedule-search-result-item"
                  onClick={() => handleSelectSchedule(scheduleRow.scheduleID)}
                >
                  <div className="schedule-search-result-name">
                    {scheduleRow.scheduleName || '未命名'}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ScheduleSearch;
